/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CluTestModelBody, CluTestModelSwagger } from 'src/clu/core/services/test/cluTestModelSwagger';
import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService, GetEnv } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export const getCluPredictionUrl = (getEnvironment: GetEnv, environmentOptions: GetEnv['arguments'] = {}) => {
    const { isProduction, apiUrl } = getEnvironment({
        apiName: 'cluRuntime',
        ...environmentOptions
    });
    const stagingUrl = 'https://workflow-dice14.usc.dev.api.cog.trafficmanager.net/svc/runtime/api';

    return urlBuilder()
        .baseUrl(isProduction ? apiUrl : stagingUrl)
        .resource(':analyze-conversations')
        .queryParam('api-version', LuisConstants.Clu.RuntimeApiVersion)
        .build();
};

export const getCluPredictionCurlRequest = ({
    projectName,
    deploymentName,
    getEnvironment,
    environmentOptions,
    authorizationHeader
}: {
    projectName: string;
    getEnvironment: GetEnv;
    deploymentName: string;
    authorizationHeader: {
        key: string;
        value: string;
    };
    environmentOptions: GetEnv['arguments'];
}) => {
    const { isProduction } = getEnvironment({
        apiName: 'cluRuntime',
        ...environmentOptions
    });

    const endpointUrl = getCluPredictionUrl(getEnvironment, environmentOptions);

    return `curl -X POST "${endpointUrl}" -H "${authorizationHeader.key}: ${authorizationHeader.value ?? 'YOUR_ENDPOINT_KEY'}" ${
        isProduction ? '' : '-H "Azure-Resource-Id: SET"'
    } -H "Apim-Request-Id: 4ffcac1c-b2fc-48ba-bd6d-b69d9942995a" -H "Content-Type: application/json" -d "${JSON.stringify(
        getTestBody('PARTICIPANT_ID_HERE', 'YOUR_QUERY_HERE', 'QUERY_LANGUAGE_HERE', projectName, deploymentName)
    )
        .split('"')
        .join('\\"')}"`;
};

export const getTestBody = (
    id: string,
    testQuery: string,
    culture: string,
    projectName: string,
    deploymentName: string
): CluTestModelBody => ({
    kind: 'Conversation',
    analysisInput: {
        conversationItem: {
            id,
            text: testQuery,
            modality: 'text',
            language: culture,
            participantId: id
        }
    },
    parameters: {
        projectName,
        verbose: true,
        deploymentName,
        stringIndexType: 'TextElement_V8'
    }
});

export const getCluTestModelService = createService(({ context, getEnvironment }) => {
    const { isProduction } = getEnvironment({ apiName: 'cluRuntime' });

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .useCustomHeader('apim-request-id', '4ffcac1c-b2fc-48ba-bd6d-b69d9942995a')
        .build();

    const test = async ({
        id,
        testQuery,
        culture,
        projectName,
        deploymentName
    }: {
        id: string;
        culture: string;
        testQuery: string;
        projectName: string;
        deploymentName: string;
    }) => {
        const { data } = await httpService.post<CluTestModelSwagger>(
            getCluPredictionUrl(getEnvironment),
            getTestBody(id, testQuery, culture, projectName, deploymentName),
            apiOptions
        );

        return data.result;
    };

    return { test };
});
